import React from 'react'
import clsx from 'clsx'
import Typography from '@material-ui/core/Typography'
import Check from '@material-ui/icons/Check'
import { Skeleton } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { Vbox } from '../layout/Box'

// Local replacement for react-storefront/option/SwatchProductOption.
// Renders a circular swatch button (color fill or image) with an optional
// selected check overlay and label.
const DefaultImage = ({ className, classes, fill, aspectRatio, ...props }) => (
  <img className={clsx(className, classes && classes.image)} {...props} />
)

const useStyles = makeStyles(
  theme => ({
    root: {},
    button: {
      position: 'relative',
      marginBottom: theme.spacing(0.5),
      padding: 2,
      borderRadius: '50%',
      backgroundColor: 'transparent',
      minWidth: 0,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.palette.grey[500],
      cursor: 'pointer',
      '&:focus': {
        outline: 0,
      },
    },
    buttonDisabled: {
      cursor: 'default',
      borderColor: theme.palette.grey['A100'],
    },
    image: {
      height: '100%',
      width: '100%',
      borderRadius: '50%',
    },
    '@media not all and (hover: none)': {
      SwatchButton: {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    checkMark: {
      transition: 'opacity 0.1s linear',
      opacity: 0,
      position: 'absolute',
      zIndex: 1,
      color: 'white',
      top: 2,
      left: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.2)',
      padding: 2,
      height: 'calc(100% - 4px)',
      width: 'calc(100% - 4px)',
      boxSizing: 'border-box',
      borderRadius: '50%',
      '.rsf-po-selected &': {
        opacity: 1,
      },
    },
    selected: {
      opacity: 1,
    },
    selectedLabel: {
      fontWeight: 'bold',
    },
    default: {
      height: 48,
      width: 48,
      '& svg': { height: 24, width: 24 },
    },
    small: {
      height: 32,
      width: 32,
      '& svg': { height: 16, width: 16 },
    },
    tiny: {
      height: 24,
      width: 24,
      '& svg': { height: 12, width: 12 },
    },
    disabled: {
      opacity: 0.3,
    },
    strikeThrough: {
      height: '7px',
      borderWidth: '2px 0',
      borderStyle: 'solid',
      borderColor: '#f2f2f2',
      backgroundColor: '#666',
      position: 'relative',
      width: '100%',
      borderRadius: 10,
    },
    defaultStrikeThrough: { top: -24 },
    smallStrikeThrough: { top: -16 },
    tinyStrikeThrough: { top: -12 },
  }),
  { name: 'RSFSwatchProductOption' },
)

export default function SwatchProductOption({
  selected,
  label,
  classes,
  color,
  imageProps = {},
  onClick,
  SelectedIcon = Check,
  size = 'default',
  skeleton,
  ImageComponent = DefaultImage,
  className,
  buttonProps = {},
  disabled,
  strikeThroughDisabled = false,
  strikeThroughAngle = 45,
}) {
  classes = useStyles({ classes })

  if (skeleton) {
    return (
      <Skeleton
        variant="circle"
        style={{ marginTop: 0, marginBottom: 4, marginLeft: 4, marginRight: 4 }}
        className={clsx({ [buttonProps.className]: true, [classes[size]]: true })}
      />
    )
  }

  return (
    <Vbox className={classes.root}>
      <button
        {...buttonProps}
        type="button"
        onClick={disabled ? () => {} : onClick}
        className={clsx({
          [className]: className != null,
          [classes.button]: true,
          [classes[size]]: true,
          [classes.buttonDisabled]: disabled,
        })}
      >
        <div className={clsx({ [classes.checkMark]: true, [classes.selected]: selected })}>
          <SelectedIcon className={classes.icon} />
        </div>
        {color ? (
          <div
            className={clsx({ [classes.image]: true, [classes.disabled]: disabled })}
            style={{ backgroundColor: color }}
          />
        ) : (
          <ImageComponent
            className={clsx({ [classes.disabled]: disabled })}
            classes={{ image: classes.image }}
            fill
            aspectRatio={1}
            {...imageProps}
          />
        )}
        {disabled && strikeThroughDisabled && (
          <div
            className={clsx({
              [classes.strikeThrough]: true,
              [classes[`${size}StrikeThrough`]]: disabled,
            })}
            style={{ transform: `rotate(${strikeThroughAngle}deg)` }}
          />
        )}
      </button>
      {label && (
        <Typography
          variant="caption"
          className={clsx({ [classes.selectedLabel]: selected })}
        >
          {label}
        </Typography>
      )}
    </Vbox>
  )
}
